import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ShieldCheck, Loader2 } from "lucide-react";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import { buildAuthRedirectUrl } from "@/lib/auth";
import { useSession } from "@/context/SessionContext";

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { status: sessionStatus } = useSession();
  const [inviteToken, setInviteToken] = useState(null);
  const [inviteEmail, setInviteEmail] = useState(null);

  useEffect(() => {
    const token = searchParams.get("invite");
    if (token) {
      setInviteToken(token);
      sessionStorage.setItem("pending_invite_token", token);
      const email = searchParams.get("email");
      if (email) setInviteEmail(email);
    }
  }, [searchParams]);

  useEffect(() => {
    const code = searchParams.get("code");
    const hash = window.location.hash;

    if (code) {
      navigate(`/auth/callback?code=${code}`, { replace: true });
      return;
    }
    if (hash && (hash.includes("access_token=") || hash.includes("refresh_token="))) {
      navigate(`/auth/callback${hash}`, { replace: true });
      return;
    }
    if (sessionStatus === "authenticated") {
      navigate("/dashboard", { replace: true });
    }
  }, [navigate, searchParams, sessionStatus]);

  const handleLogin = async () => {
    const redirectTo = buildAuthRedirectUrl();
    console.log("[LoginPage] OAuth redirectTo:", redirectTo);
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo },
    });
  };

  if (sessionStatus === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl"></div>
      </div>
      
      <div className="relative z-10 w-full max-w-md px-6">
        <div className="text-center mb-12">
          <h1 className="font-heading text-5xl md:text-6xl font-bold tracking-tight mb-4">
            Investing OS
          </h1>
          <p className="text-lg text-muted-foreground">
            Your power-user portfolio command center
          </p>
        </div>

        <div className="bg-card border border-border rounded-lg p-8 backdrop-blur-xl bg-card/60">
          {inviteToken && (
            <div className="flex items-center gap-2 mb-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20" data-testid="invite-banner">
              <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0" />
              <div>
                <p className="text-sm font-medium text-emerald-300">You've been invited</p>
                <p className="text-xs text-emerald-400/70">
                  Sign in with Google{inviteEmail ? ` (${inviteEmail})` : ""} to accept your invite
                </p>
              </div>
            </div>
          )}

          {!isSupabaseConfigured && (
            <p className="text-xs text-amber-400 mb-4 text-center">
              Auth is not configured on this deploy. Set Supabase env vars in Cloudflare Pages.
            </p>
          )}

          <button
            data-testid="google-signin-button"
            onClick={handleLogin}
            disabled={!isSupabaseConfigured}
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium py-3 px-6 rounded-md transition-colors flex items-center justify-center gap-3 disabled:opacity-50"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            {inviteToken ? "Accept Invite & Sign In" : "Sign in with Google"}
          </button>

          <p className="text-xs text-muted-foreground text-center mt-6">
            {inviteToken ? "Your access will be activated after sign-in" : "Secure authentication via Google"}
          </p>
        </div>
      </div>
    </div>
  );
}
